// P10.3 — Skippable guided App Tour (coach-marks) for the POS surface. Persists "tour_seen" on the
// user profile via /pos/me/ui-flags (NOT localStorage). Mounted by OfflineStatusBar (POS pages only).
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Compass, X } from "lucide-react";

const TOUR = [
  { title: "Scan & search", body: "Scan a barcode or type a SKU here to add items to the cart." },
  { title: "Cart & totals", body: "Review lines, quantities, discounts and live GST totals." },
  { title: "Tenders", body: "Split across cash, card and UPI — the drawer can open automatically." },
  { title: "Shift", body: "Open/close shift and reconcile cash from the shift menu." },
  { title: "Settings", body: "Configure printers, profiles and preferences in Device Settings." },
];

export function AppTour() {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [prompted, setPrompted] = useState(false);

  useEffect(() => {
    let active = true;
    if (!localStorage.getItem("reason_token")) return;
    api.get("/pos/me/ui-flags").then(({ data }) => {
      if (active && !(data?.data?.tour_seen)) setPrompted(true);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const persist = () => api.post("/pos/me/ui-flags", { flags: { tour_seen: true } }).catch(() => {});
  const startTour = () => { setShow(true); setPrompted(false); setStep(0); };
  const finish = () => { setShow(false); setPrompted(false); persist(); };

  if (prompted) {
    return (
      <div className="fixed bottom-20 right-4 z-[70] w-72 rounded-lg border bg-background p-4 shadow-lg" data-testid="app-tour-prompt">
        <p className="flex items-center gap-2 font-medium text-sm"><Compass className="h-4 w-4 text-primary" /> New to RetailOS POS?</p>
        <p className="text-xs text-muted-foreground mt-1">Take a 30-second tour of the billing screen.</p>
        <div className="flex gap-2 mt-3">
          <Button size="sm" onClick={startTour} data-testid="app-tour-start">Take a tour</Button>
          <Button size="sm" variant="ghost" onClick={finish} data-testid="app-tour-skip">Skip</Button>
        </div>
      </div>
    );
  }
  if (!show) return null;
  const s = TOUR[step];
  const last = step === TOUR.length - 1;
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-end sm:items-center justify-center p-4" data-testid="app-tour-overlay">
      <div className="w-full max-w-sm rounded-lg bg-background p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Step {step + 1} / {TOUR.length}</span>
          <button onClick={finish} data-testid="app-tour-close"><X className="h-4 w-4" /></button>
        </div>
        <h3 className="text-lg font-semibold mt-1">{s.title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{s.body}</p>
        <div className="flex justify-end gap-2 mt-4">
          {step > 0 && <Button size="sm" variant="ghost" onClick={() => setStep(step - 1)} data-testid="app-tour-prev">Back</Button>}
          {last ? <Button size="sm" onClick={finish} data-testid="app-tour-done">Done</Button>
                : <Button size="sm" onClick={() => setStep(step + 1)} data-testid="app-tour-next">Next</Button>}
        </div>
      </div>
    </div>
  );
}
